/**
 * Profile Questionnaire — pure model.
 *
 * Elm-ish doctrine: state is explicit and typed. No `useState` copies
 * in components, no hidden transitions in render bodies, no imperative
 * API calls from the view. The reducer in `update.ts` is the only
 * state-mutation site.
 *
 * Security posture:
 *   - Identity-minimized fields (display_name, headline, bio,
 *     city_or_region). No legal name, email, phone, address, employer,
 *     or social profiles.
 *   - Photos are durable server-canonical state in this ACT
 *     (B-2.B ACT-INDEEP-B2-B-PROFILE-MEDIA-FRONTEND01). The FE never
 *     fabricates photo ids / urls / positions; the server response is
 *     the canonical authority.
 *
 * Modes:
 *   - `create` : new user (no existing profile loaded). On success, the
 *     completion modal opens exactly once.
 *   - `edit`   : existing user profile is prefilled. On success, the
 *     modal does NOT open.
 *
 * Completion-modal idempotence (per §37 of the briefing):
 *   `completionModalShown` is set ONLY on the first SaveSucceeded of a
 *   CREATE-mode transition that produced `profileStatus === 'active'`.
 *   Refreshing / reloading an already-completed profile never reopens
 *   the modal.
 */

import type { InternalScenario, InternalGender } from './rolePresentation';
import { PHOTO_MAX_COUNT } from '@/api/profilePhotos';
import type { Skill } from '@/api/skills';

export type ProfileFormMode =
  | { readonly kind: 'create' }
  | { readonly kind: 'edit'; readonly profileId: string };

export type ProfileFormStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'saving'
  | 'saved'
  | 'error';

/**
 * Photo state slot.
 *
 * Three categories (per ACT §3):
 *
 *   A. Durable server photo  (kind = 'persisted')
 *      - id / url / position are server-canonical; do NOT fabricate.
 *
 *   B. Local pending upload  (kind = 'new-local' | 'uploading' | 'failed-upload')
 *      - The localId is a transient FE-only identifier.
 *      - `file` is the browser File; lost on reload.
 *      - `previewUrl` is a URL.createObjectURL result; revoked on
 *        success / cancel / teardown.
 *
 *   C. Delete-in-flight      (kind = 'deleting')
 *      - Optimistic delete marker; replaced by the canonical list
 *        when the server returns the post-delete photo list.
 */
export interface PhotoEntry {
  /** Photo UUID when durable / transient local-id when pending. */
  readonly id: string;
  readonly kind:
    | 'persisted'
    | 'new-local'
    | 'uploading'
    | 'failed-upload'
    | 'deleting';
  /**
   * For persisted: server-canonical content URL.
   * For new-local / uploading / failed-upload: object-URL preview.
   * For deleting: server-canonical content URL (still renders until
   * server list arrives).
   */
  readonly url: string;
  /** Server-canonical position when durable; otherwise transient. */
  readonly position?: number;
  /** Original user-visible filename (UX only). */
  readonly fileName?: string;
  /** Local File object when pending / uploading. */
  readonly file?: File;
  /** Server-canonical error message when failed-upload. */
  readonly errorMessage?: string;
}

export interface ProfileForm {
  readonly display_name?: string;
  readonly gender?: InternalGender;
  readonly scenario?: InternalScenario;
  readonly age?: number;
  readonly city?: string;
  readonly headline?: string;
  readonly bio?: string;
  readonly work_sphere?: string;
  readonly education_sphere?: string;
  readonly education_type?: string;
}

export interface ProfileFormModel {
  readonly mode: ProfileFormMode;
  readonly status: ProfileFormStatus;
  readonly form: ProfileForm;
  readonly original: ProfileForm | null;
  /** Original photo list (persisted). For EDIT-mode prefill. */
  readonly persistedPhotos: readonly PhotoEntry[];
  /** Transient photo state (new-local / uploading / failed-upload / deleting). */
  readonly transientPhotos: readonly PhotoEntry[];
  readonly validationErrors: readonly import('./validation').FieldValidationError[];
  readonly missingActivationFields: readonly string[];
  readonly profileStatus: 'inactive' | 'active' | 'blocked' | 'deleted';
  readonly dirty: boolean;
  readonly hasProfile: boolean;
  readonly serverError: string | null;
  /**
   * Discriminator for `serverError`. Mirrors the optional `code`
   * on the `SubmitFailed` message and lets the view distinguish
   * "you must complete onboarding first" from a real save failure.
   * Cleared when `serverError` is cleared.
   */
  readonly serverErrorCode:
    | 'role_not_classified'
    | 'equal_role_not_implemented'
    | 'network_error'
    | 'validation_error'
    | 'unknown'
    | null;
  /** Completion modal idempotence flag (§37 of briefing). */
  readonly completionModalOpen: boolean;
  /** Show the S005 Step3TransitionScreen after success. */
  readonly s005TransitionShown: boolean;
  /** Server returned that this is the first successful completion in CREATE mode. */
  readonly justCompletedFirstTime: boolean;
  // ---------------------------------------------------------------------------
  // B-2.C skills / preferences slots
  // ---------------------------------------------------------------------------
  /**
   * Taxonomy list state. `items` is server-canonical (popularity DESC,
   * code ASC). FE never hardcodes labels; this is the only source of
   * labels/categories in the picker.
   */
  readonly skillsTaxonomy:
    | { readonly status: 'idle' }
    | {
        readonly status: 'loading';
        readonly items: readonly Skill[];
        readonly error: string | null;
      }
    | {
        readonly status: 'ready';
        readonly items: readonly Skill[];
        readonly error: string | null;
      }
    | {
        readonly status: 'failed';
        readonly items: readonly Skill[];
        readonly error: string;
      };
  /** User selection order (user-controlled). Always unique. */
  readonly selectedSkillIds: readonly string[];
  /** Server-canonical selected skill IDs (for prefill + diff). */
  readonly persistedSkillIds: readonly string[];
  /** Save lifecycle: idle / saving / failed. */
  readonly skillsSave: 'idle' | 'saving' | 'failed';
  /** Most recent save failure message; cleared on success. */
  readonly skillsError: string | null;
  /** Search input (case-insensitive label match). */
  readonly skillsSearch: string;
  /** Currently-active category filter (null = all). */
  readonly skillsCategory: string | null;
}

/** Initial empty model — CREATE mode. */
export const initialCreateModel: ProfileFormModel = {
  mode: { kind: 'create' },
  status: 'idle',
  form: {},
  original: null,
  persistedPhotos: [],
  transientPhotos: [],
  validationErrors: [],
  missingActivationFields: [],
  profileStatus: 'inactive',
  dirty: false,
  hasProfile: false,
  serverError: null,
  serverErrorCode: null,
  completionModalOpen: false,
  s005TransitionShown: false,
  justCompletedFirstTime: false,
  // B-2.C skills slots
  skillsTaxonomy: { status: 'idle' },
  selectedSkillIds: [],
  persistedSkillIds: [],
  skillsSave: 'idle',
  skillsError: null,
  skillsSearch: '',
  skillsCategory: null,
};

/**
 * Initial model for EDIT mode. Caller must dispatch `Started` to load
 * the existing profile into the model.
 */
export function initialEditModel(profileId: string): ProfileFormModel {
  return {
    ...initialCreateModel,
    mode: { kind: 'edit', profileId },
  };
}

/**
 * Compute visible slot count = durable + pending.
 *
 * Per ACT §11 the user must not be able to queue enough local
 * uploads to trivially exceed PHOTO_MAX_COUNT (6). The server
 * remains the final authority (server-enforced per-profile row
 * lock).
 */
export function visibleSlotCount(model: ProfileFormModel): number {
  let n = model.persistedPhotos.filter((p) => p.kind !== 'deleting').length;
  for (const t of model.transientPhotos) {
    if (
      t.kind === 'new-local' ||
      t.kind === 'uploading' ||
      t.kind === 'failed-upload'
    ) {
      n += 1;
    }
  }
  return n;
}

/**
 * True when the add-photo affordance should be disabled. The server
 * still owns the authoritative max-count; this is UX-only.
 */
export function isPhotoSlotExhausted(model: ProfileFormModel): boolean {
  return visibleSlotCount(model) >= PHOTO_MAX_COUNT;
}

/**
 * Compute dirty: true iff the current form differs from the loaded
 * original (or, in CREATE mode, any field is non-empty).
 */
export function computeDirty(
  current: ProfileForm,
  original: ProfileForm | null,
  persistedPhotos: readonly PhotoEntry[],
  transientPhotos: readonly PhotoEntry[],
  selectedSkillIds: readonly string[] = [],
  persistedSkillIds: readonly string[] = []
): boolean {
  const formChanged =
    original === null
      ? Object.values(current).some((v) => v !== undefined && v !== '')
      : (current.display_name ?? '') !== (original.display_name ?? '') ||
        (current.gender ?? '') !== (original.gender ?? '') ||
        (current.scenario ?? '') !== (original.scenario ?? '') ||
        (current.age ?? 0) !== (original.age ?? 0) ||
        (current.city ?? '') !== (original.city ?? '') ||
        (current.headline ?? '') !== (original.headline ?? '') ||
        (current.bio ?? '') !== (original.bio ?? '') ||
        (current.work_sphere ?? '') !== (original.work_sphere ?? '') ||
        (current.education_sphere ?? '') !== (original.education_sphere ?? '') ||
        (current.education_type ?? '') !== (original.education_type ?? '');
  const photosChanged =
    transientPhotos.length > 0 ||
    persistedPhotos.some((p) => p.kind === 'deleting');
  const skillsChanged =
    selectedSkillIds.length !== persistedSkillIds.length ||
    selectedSkillIds.some((id, i) => id !== persistedSkillIds[i]);
  return formChanged || photosChanged || skillsChanged;
}
